import React, { useState, useEffect } from 'react';
import PrimaryNavigation from '../../components/ui/PrimaryNavigation';
import NavigationBreadcrumb from '../../components/ui/NavigationBreadcrumb';
import IntegrationCard from './components/IntegrationCard';
import api from '../../api/axios';

const categoryConfig = {
  ECOMMERCE: { name: 'E-Commerce', icon: 'ShoppingBag', iconColor: 'var(--color-primary)' },
  LOGISTICS: { name: 'Logistics & Delivery', icon: 'Truck', iconColor: 'var(--color-warning)' },
  PAYMENTS: { name: 'Payments', icon: 'CreditCard', iconColor: 'var(--color-success)' },
  COMMUNICATION: { name: 'Messaging Channels', icon: 'MessageCircle', iconColor: 'var(--color-accent)' },
};

const categoryOrder = ['ECOMMERCE', 'LOGISTICS', 'PAYMENTS', 'COMMUNICATION'];

const Integration = () => {
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchIntegrations();
  }, []);

  const fetchIntegrations = async () => {
    try {
      const response = await api.get('/api/v1/integrations');
      setIntegrations(response.data?.data || []);
    } catch (error) {
      console.error('Failed to fetch integrations:', error);
    } finally {
      setLoading(false);
    }
  };

  // Group integrations by category
  const grouped = integrations.reduce((acc, item) => {
    const cat = item.category || 'OTHER';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {});

  // Build category cards: status is "connected" if any integration in that category is connected
  const categories = categoryOrder
    .filter((cat) => grouped[cat] && grouped[cat].length > 0)
    .map((cat) => {
      const items = grouped[cat];
      const hasConnected = items.some((i) => i.status === 'CONNECTED');
      const config = categoryConfig[cat] || { name: cat, icon: 'Plug', iconColor: 'var(--color-muted-foreground)' };
      return {
        id: cat,
        name: config.name,
        icon: config.icon,
        iconColor: config.iconColor,
        brands: items.map((i) => ({
          name: i.name,
          logo: brandIconMap[i.type] || 'Plug',
          status: i.status,
        })),
        status: hasConnected ? 'connected' : 'not-connected',
      };
    });

  return (
    <>
      <PrimaryNavigation />
      <div className="main-content">
        <div className="min-h-screen bg-background">
          <NavigationBreadcrumb
            items={[
              { label: 'Home', path: '/' },
              { label: 'Integration', path: '/integration' }
            ]}
          />

          <div className="p-4 md:p-6 lg:p-8">
            {/* Header */}
            <div className="mb-6">
              <h1 className="text-h2 font-heading text-foreground mb-2">
                Integration Management
              </h1>
              <p className="text-muted-foreground">
                Connect and manage your third-party integrations across different categories
              </p>
            </div>

            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="bg-card border border-border rounded-lg p-6 animate-pulse">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 rounded-lg bg-muted" />
                      <div className="h-5 w-24 bg-muted rounded" />
                    </div>
                    <div className="h-4 w-full bg-muted rounded mb-3" />
                    <div className="flex gap-3 mb-4">
                      <div className="h-8 w-20 bg-muted rounded" />
                      <div className="h-8 w-20 bg-muted rounded" />
                    </div>
                    <div className="h-10 w-full bg-muted rounded" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {categories.map((category) => (
                  <IntegrationCard
                    key={category.id}
                    name={category.name}
                    icon={category.icon}
                    iconColor={category.iconColor}
                    brands={category.brands}
                    status={category.status}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

const brandIconMap = {
  SHOPIFY: 'ShoppingBag',
  SHIPROCKET: 'Package',
  SHIPWAY: 'PackageCheck',
  RAZORPAY: 'Wallet',
  STRIPE: 'CreditCard',
  WHATSAPP: 'MessageSquare',
  KWIKENGAGE: 'Zap',
};

export default Integration;
